import type { ThemeSettings } from '@cognitoforms/types/server-types/forms/configuration/theme-settings';
import type { WorkflowAction } from '@cognitoforms/types/server-types/forms/model/workflow-action';
import type { Order } from '@cognitoforms/types/server-types/payment/model/order';
import type { EntryDocument, GuestResult } from 'src/web-api/entry-service';
import type { FormEvent } from '../eventing/form-event';
import type { EntityArgsOfType } from '../../../../../libs/model.js/@types';
import type { FormEntry } from '@cognitoforms/types/server-types/forms/model/form-entry';

export enum FormEvents {
	BeforeSubmit = 'beforeSubmit',
	AfterSubmit = 'afterSubmit',
	BeforeNavigate = 'beforeNavigate',
	AfterNavigate = 'afterNavigate',
	UploadFile = 'uploadFile',
	Resize = 'resize',
	AfterSave = 'afterSave',
	// Below events are not necessarily public
	Redirect = 'redirect',
	Ready = 'ready',
	ResetEntry = 'resetEntry',
	Validate = 'validate',
	Prefill = 'prefill',
	CaptureHost = 'captureHost',
	SetCss = 'setCss',
	OverrideText = 'overrideText',
	EntryToken = 'entryToken',
	InitGoogleAnalytics = 'InitGoogleAnalytics',
	IframeGoogleAnalyticsSubmissionEvent = 'IframeGoogleAnalyticsSubmissionEvent',
	IframeGoogleAnalyticsSubmissionRecordedEvent = 'IframeGoogleAnalyticsSubmissionRecordedEvent',
	ScrollToTop = 'scrollToTop',
	ThemeUpdated = 'themeUpdated',
	FailedValidation = 'failedValidation',
	AllowedActionsChanged = 'allowedActionsChanged',
	ActionChanged = 'actionChanged',
	OrderUpdated = 'orderUpdated',
	ActivityPerformed = 'activityPerformed',
	TryDownloadOverLimit = 'tryDownloadOverLimit',
	FormPreviewUpdated = 'formPreviewUpdated',
	CreateToastMessage = 'createToastMessage'
}

type EntryData = EntityArgsOfType<FormEntry>;

export interface ReadyEventData {
	entry: EntryData;
}

export type ReadyEvent = FormEvent<ReadyEventData> & { type: FormEvents.Ready };

export interface CaptureHostEventData {
	url: string;
}

export interface BeforeSubmitEventData {
	hasErrors: boolean;
	entry: EntryData;
}

export type BeforeSubmitEvent = FormEvent<BeforeSubmitEventData> & { type: FormEvents.BeforeSubmit };

export interface AfterSubmitEventData {
	entry: EntryData;
	documents: Array<EntryDocument>;
	auditRecordId?: string;
	convertedMessage?: string;
	guestResult?: GuestResult;
}

export type AfterSubmitEvent = FormEvent<AfterSubmitEventData> & { type: FormEvents.AfterSubmit };

export enum NavigationDirection {
	Forward = 'forward',
	Backward = 'backward'
}

export interface FormPage {
	number: number;
	title: string;
	name: string;
}

export interface BeforeNavigateEventData {
	entry: EntryData;
	direction: NavigationDirection;
	sourcePage: FormPage;
	destinationPage: FormPage;
}

export type BeforeNavigateEvent = FormEvent<BeforeNavigateEventData> & { type: FormEvents.BeforeNavigate };

export interface AfterNavigateEventData {
	entry: EntryData;
	direction: NavigationDirection;
	sourcePage: FormPage;
	destinationPage: FormPage;
}

export type AfterNavigateEvent = FormEvent<AfterNavigateEventData> & { type: FormEvents.AfterNavigate };

export interface InitGoogleAnalyticsEventData {
	googleAnalyticsTrackingId: string;
	formInternalName: string;
	isMultipageForm: boolean;
	firstVisiblePageName: string;
}

export type InitGoogleAnalyticsEvent = FormEvent<InitGoogleAnalyticsEventData> & { type: FormEvents.InitGoogleAnalytics };

export interface IframeGoogleAnalyticsSubmissionEventEventData {
	goalLabel: string;
	goalValue: number;
}

export type IframeGoogleAnalyticsSubmissionEvent = FormEvent<IframeGoogleAnalyticsSubmissionEventEventData> & { type: FormEvents.IframeGoogleAnalyticsSubmissionEvent };

export interface IframeGoogleAnalyticsSubmissionRecordedEventData extends IframeGoogleAnalyticsSubmissionEventEventData {
	successful: boolean;
}

export type IframeGoogleAnalyticsSubmissionRecordedEvent = FormEvent<IframeGoogleAnalyticsSubmissionRecordedEventData> & { type: FormEvents.IframeGoogleAnalyticsSubmissionRecordedEvent };

export interface FormFile {
	// The name of the uploaded file
	name: string;
	// The Cognito Forms assigned id of the file
	id: string;
	// The file size in bytes
	size: number;
}

export interface UploadFileEventData {
	file: FormFile;
}

export type UploadFileEvent = FormEvent<UploadFileEventData> & { type: FormEvents.UploadFile };

export interface ThemeUpdatedEventData {
	themeSettings: ThemeSettings;
}

export interface AllowedActionsChangedEventData {
	allowedActions: WorkflowAction[];
}

export type AllowedActionsChangedEvent = FormEvent<AllowedActionsChangedEventData> & { type: FormEvents.AllowedActionsChanged };

export interface ActionChangedEventData {
	action: WorkflowAction;
}

export type ActionChangedEvent = FormEvent<ActionChangedEventData> & { type: FormEvents.ActionChanged };

export interface OrderUpdatedEventData {
	order: Order;
}

export type OrderUpdatedEvent = FormEvent<OrderUpdatedEventData> & { type: FormEvents.OrderUpdated };

export interface FormPreviewUpdatedEventData {
	status?: string;
	role?: string;
}

export type FormPreviewUpdatedEvent = FormEvent<FormPreviewUpdatedEventData> & { type: FormEvents.FormPreviewUpdated };

export interface CreateToastMessageEventData {
	toastType?: string;
	toastMessage?: string;
}

export type CreateToastMessageEvent = FormEvent<CreateToastMessageEventData> & { type: FormEvents.CreateToastMessage };
